
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/SafeAuthProvider";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSettings } from "@/context/SettingsContext";
import HCaptcha from "@hcaptcha/react-hcaptcha";

type AuthMode = "signin" | "signup";

interface AuthFormProps {
  className?: string;
}

const AuthForm = ({ className }: AuthFormProps) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<AuthMode>("signin");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { signIn, signUp, loading } = useAuth();
  const { darkMode } = useSettings();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaToken) {
      return; // Don't submit without captcha
    }
    
    if (mode === "signin") {
      await signIn(email, password, captchaToken);
    } else {
      await signUp(email, password, captchaToken);
    }
  };

  const handleCaptchaVerify = (token: string) => {
    setCaptchaToken(token);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
  };

  return (
    <div className={cn("w-full max-w-md mx-auto", className)}>
      <div className={cn(
        "text-center mb-6",
        darkMode ? "text-[#FBF3F1]" : "text-[#1E0C46]"
      )}>
        <h2 className="text-2xl font-bold">
          {mode === "signin" ? "Welcome Back" : "Create Account"}
        </h2>
        <p className="text-sm mt-2">
          {mode === "signin" 
            ? "Sign in to access your journaling prompts" 
            : "Sign up to save your progress and journal entries"}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={cn(
              "w-full",
              darkMode ? "bg-[#2d1a45]/60 border-[#6127A0]/50 text-white" : ""
            )}
            disabled={loading}
            required
          />
        </div>
        
        <div>
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={cn(
              "w-full",
              darkMode ? "bg-[#2d1a45]/60 border-[#6127A0]/50 text-white" : ""
            )}
            disabled={loading}
            required
          />
        </div>
        
        <div className="flex justify-center">
          <HCaptcha
            sitekey="e0cce552-8a68-4aee-85a6-149c9eb20da8"
            onVerify={handleCaptchaVerify}
            onExpire={handleCaptchaExpire}
            theme={darkMode ? "dark" : "light"}
          />
        </div>
        
        <Button
          type="submit"
          className={cn(
            "w-full",
            darkMode ? "bg-[#6127A0] hover:bg-[#4d1f80]" : "bg-[#6127A0] text-white hover:bg-[#4d1f80]"
          )}
          disabled={loading || !captchaToken}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : mode === "signin" ? (
            "Sign In"
          ) : (
            "Sign Up"
          )}
        </Button>
      </form>
      
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          className={cn(
            "text-sm underline",
            darkMode ? "text-[#AE78F0] hover:text-[#c096ff]" : "text-[#6127A0] hover:text-[#4d1f80]"
          )}
          disabled={loading}
        >
          {mode === "signin"
            ? "Don't have an account? Sign up"
            : "Already have an account? Sign in"}
        </button>
      </div>
    </div>
  );
};

export default AuthForm;
